/**
 * @fileoverview Arama Gecmisi Yonetimi Hook'u
 *
 * Bu modul, kullanicinin arama gecmisini yoneten hook'u icerir.
 * Gecmis, widget state'inde modelContent.searchHistory altinda saklanir.
 *
 * @module hooks/useSearchHistory
 */

import { useCallback, useMemo } from "react";
import { LIMITS } from "../utils";

/**
 * @typedef {Object} SearchHistoryEntry
 * @property {string} path - Arama yolu (ornegin: "satilik-konut/istanbul")
 * @property {string|null} filters - Filtre string'i
 * @property {string} at - ISO 8601 tarih string'i
 */

/**
 * @typedef {Object} UseSearchHistoryReturn
 * @property {SearchHistoryEntry[]} searchHistory - Arama gecmisi
 * @property {Function} addToHistory - Yeni arama ekle
 */

/**
 * Arama gecmisi yonetimi hook'u.
 *
 * Bu hook, arama gecmisini okuma ve yeni arama ekleme islevlerini saglar.
 * Tekrarlayan aramalar otomatik olarak filtrelenir ve gecmis
 * LIMITS.MAX_SEARCH_HISTORY ile sinirlidir.
 *
 * @function useSearchHistory
 * @param {Object} widgetState - Mevcut widget state
 * @param {Function} updateWidgetState - Widget state guncelleme fonksiyonu
 * @returns {UseSearchHistoryReturn} Arama gecmisi ve yonetim fonksiyonlari
 *
 * @example
 * const { searchHistory, addToHistory } = useSearchHistory(widgetState, updateWidgetState);
 *
 * // Yeni arama ekle
 * addToHistory({ path: "satilik-konut/istanbul", filters: "oda-sayisi=3-1" });
 */
export function useSearchHistory(widgetState, updateWidgetState) {
  // Arama gecmisini state'den al
  const searchHistory = useMemo(
    () => widgetState?.modelContent?.searchHistory || [],
    [widgetState?.modelContent?.searchHistory]
  );

  // Yeni arama ekle (deduplication ile)
  const addToHistory = useCallback(
    (entry) => {
      updateWidgetState((state) => {
        const normalizedEntry = {
          path: entry.path,
          filters: entry.filters ?? null,
          at: entry.at || new Date().toISOString(),
        };

        const prev = Array.isArray(state.modelContent.searchHistory)
          ? state.modelContent.searchHistory
          : [];

        // Ayni path ve filters kombinasyonunu kaldir (deduplication)
        const deduped = prev.filter(
          (h) => !(h?.path === normalizedEntry.path && h?.filters === normalizedEntry.filters)
        );

        // Yeni aramayi basa ekle ve limiti uygula
        const nextHistory = [normalizedEntry, ...deduped].slice(0, LIMITS.MAX_SEARCH_HISTORY);

        return {
          ...state,
          modelContent: {
            ...state.modelContent,
            searchHistory: nextHistory,
          },
        };
      });
    },
    [updateWidgetState]
  );

  return { searchHistory, addToHistory };
}

export default useSearchHistory;
